<template lang="pug">
div.map-base
  .map-slot.map-slot-top-left(v-if="$slots['top-left']")
    slot(
      name="top-left",
      :marcadorSelecionado="marcadorSelecionado",
      :deselecionarMarcador="deselecionarMarcador"
    )
  .map-slot.map-slot-top-right(v-if="$slots['top-right']")
    slot(
      name="top-right",
      :marcadorSelecionado="marcadorSelecionado",
      :deselecionarMarcador="deselecionarMarcador"
    )
  .map-slot.map-slot-bottom-left(v-if="$slots['bottom-left']")
    slot(
      name="bottom-left",
      :marcadorSelecionado="marcadorSelecionado",
      :deselecionarMarcador="deselecionarMarcador"
    )
  .map-slot.map-slot-bottom-right(v-if="$slots['bottom-right']")
    slot(
      name="bottom-right",
      :marcadorSelecionado="marcadorSelecionado",
      :deselecionarMarcador="deselecionarMarcador"
    )
  l-map(
    ref="map",
    class="map-canvas",
    :zoom="zoom",
    :center="center",
    :options="mapOptions"
  )

  div.map-overlay(v-if="loading || erroBasemap || semDados")
    v-card.map-overlay-card(elevation="6")
      v-card-text.text-center.pa-6(v-if="loading")
        v-progress-circular(:size="60", color="green", indeterminate)
        .subtitle-1.font-weight-bold.mt-3 Carregando...
      v-card-text.text-center.pa-6(v-else-if="semDados")
        v-icon(color="grey", x-large) mdi-map-marker-off
        .subtitle-1.font-weight-bold.mt-3 Nenhum dado encontrado
        .body-2.grey--text.mt-2 Não há localizações para exibir
      v-card-text.text-center.pa-6(v-else)
        v-icon(color="orange darken-2", x-large) mdi-alert-circle-outline
        .subtitle-1.font-weight-bold.mt-3 Erro ao carregar o mapa
        .body-2.grey--text.mt-2 Verifique a chave de API do ArcGIS
</template>

<script>
import "leaflet/dist/leaflet.css";
import { LMap } from "vue2-leaflet";
import L from "leaflet";
import { vectorBasemapLayer } from "esri-leaflet-vector";
import {
  MAP_CONFIG,
  BASEMAP_STYLE,
  BRAZIL_BOUNDS,
  ARCGIS_API_KEY,
  MARKER_STYLE,
  MARKER_SELECTED_STYLE,
  getMapOptions,
  getBasemapOptions
} from "./mapConfig";
import { Spiderfier } from "./mapSpiderfier";

export default {
  name: "MapBaseLeaflet",

  components: {
    LMap
  },

  props: {
    marcadores: {
      type: Array,
      default: () => []
    },
    loading: {
      type: Boolean,
      default: false
    },
    semDados: {
      type: Boolean,
      default: false
    },
    getMarkerColor: {
      type: Function,
      default: null
    }
  },

  data() {
    return {
      mapOptions: getMapOptions(),
      zoom: MAP_CONFIG.DEFAULT_ZOOM,
      center: MAP_CONFIG.DEFAULT_CENTER,
      erroBasemap: false,
      marcadorSelecionado: null
    };
  },

  watch: {
    marcadores() {
      this.popularMarcadores();
    }
  },

  mounted() {
    this.$nextTick(() => {
      const map = this.$refs.map.mapObject;

      try {
        const basemap = vectorBasemapLayer(BASEMAP_STYLE, getBasemapOptions(ARCGIS_API_KEY));
        basemap.on("error", () => {
          this.erroBasemap = true;
        });
        basemap.addTo(map);
      } catch {
        this.erroBasemap = true;
      }

      this._markerGroup = L.featureGroup();
      map.addLayer(this._markerGroup);

      this._spiderfier = new Spiderfier(map, {
        onUnspiderfy: () => this.limparSelecao()
      });

      map.on("drag", () => {
        map.panInsideBounds(BRAZIL_BOUNDS, { animate: false });
      });

      map.on("click", () => {
        this.deselecionarMarcador();
      });

      map.on("moveend", () => {
        this.center = [map.getCenter().lat, map.getCenter().lng];
        this.zoom = map.getZoom();
      });

      map.invalidateSize();
      this.inicializarResizeObserver();
      this.popularMarcadores();
    });
  },

  beforeDestroy() {
    if (this._spiderfier) {
      this._spiderfier.destroy();
    }
    if (this._resizeObserver) {
      this._resizeObserver.disconnect();
    }
  },

  methods: {
    corMarcador(marcador) {
      if (typeof this.getMarkerColor === "function") {
        return this.getMarkerColor(marcador);
      }
      return "#757575";
    },

    popularMarcadores() {
      if (!this._markerGroup) return;

      if (this._spiderfier && this._spiderfier.isSpiderfied()) {
        this._spiderfier.unspiderfy();
      }

      this._markerGroup.clearLayers();
      this._selectedMarker = null;
      this.limparSelecaoInterna(false);

      this.marcadores.forEach(marcador => {
        const marker = L.circleMarker(marcador.localizacao, {
          ...MARKER_STYLE,
          fillColor: this.corMarcador(marcador),
          bubblingMouseEvents: false
        });

        marker._data = marcador;
        marker.on("click", () => this.onMarkerClick(marker));
        this._markerGroup.addLayer(marker);
      });
    },

    onMarkerClick(marker) {
      const dados = marker._data;

      if (this.marcadorSelecionado === dados) {
        this.deselecionarMarcador();
        return;
      }

      const allMarkers = [];
      this._markerGroup.eachLayer(item => allMarkers.push(item));
      const nearby = this._spiderfier.findNearbyMarkers(marker, allMarkers);

      if (nearby && !this._spiderfier.isSpiderfied()) {
        this.deselecionarMarcador();
        this._spiderfier.spiderfy(nearby);
        return;
      }

      this.resetarMarcadorSelecionado();

      marker.setStyle(MARKER_SELECTED_STYLE);
      marker.bringToFront();
      this._selectedMarker = marker;
      this.marcadorSelecionado = dados;
      this.$emit("select-marker", dados);
    },

    deselecionarMarcador() {
      if (this._spiderfier && this._spiderfier.isSpiderfied()) {
        this._spiderfier.unspiderfy();
      }

      this.resetarMarcadorSelecionado();
      this.limparSelecaoInterna();
    },

    limparSelecao() {
      this.resetarMarcadorSelecionado();
      this.limparSelecaoInterna();
    },

    limparSelecaoInterna(emitir = true) {
      this.marcadorSelecionado = null;
      if (emitir) {
        this.$emit("clear-selection");
      }
    },

    resetarMarcadorSelecionado() {
      if (!this._selectedMarker) return;

      this._selectedMarker.setStyle({
        ...MARKER_STYLE,
        fillColor: this.corMarcador(this._selectedMarker._data)
      });
      this._selectedMarker = null;
    },

    inicializarResizeObserver() {
      const map = this.$refs.map.mapObject;
      this._resizeObserver = new ResizeObserver(() => {
        map.invalidateSize();
      });
      this._resizeObserver.observe(this.$refs.map.$el);
    }
  }
};
</script>

<style scoped>
.map-base {
  position: relative;
  width: 100%;
  height: 100%;
  z-index: 0;
}

.map-canvas {
  height: 100%;
}

.map-slot {
  position: absolute;
  z-index: 1000;
}

.map-slot-top-left {
  top: 10px;
  left: 10px;
}

.map-slot-top-right {
  top: 10px;
  right: 10px;
}

.map-slot-bottom-left {
  bottom: 0;
  left: 0;
}

.map-slot-bottom-right {
  right: 0;
  bottom: 0;
}

.map-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  background: rgba(0, 0, 0, 0.45);
  backdrop-filter: blur(4px);
  z-index: 1000;
}

.map-overlay-card {
  border-radius: 12px;
}

::v-deep .leaflet-container {
  background: #1e2a1a;
}
</style>
